import { useEffect, useMemo, useState } from "react";
import { useLocalSearchParams } from "expo-router";
import { Pressable, StyleSheet, useWindowDimensions, View } from "react-native";

import type {
  AdminDanceSummary,
  AdminDashboardData,
  AdminEmailMessage
} from "@recital-master/shared";
import { danceStyleLabel } from "@recital-master/shared";

import { useAuthState } from "@/auth/AuthProvider";
import { shouldRedirectFromAdmin } from "@/auth/adminRouteAccess";
import { AppHeader } from "@/components/AppHeader";
import {
  fmtFamilies,
  fmtHours,
  fmtPlays,
  fmtWatchClock
} from "@/components/admin/adminFormat";
import { DailyWatchTrend } from "@/components/admin/DailyWatchTrend";
import {
  RetentionCurve,
  RetentionFamilyBreakdown
} from "@/components/admin/RetentionCurve";
import {
  useAdminDanceRetention,
  useAdminDashboard,
  useAdminFamilyDances
} from "@/data/adminDashboard";
import { useAdminEmailActivity } from "@/data/useAdminEmailActivity";
import {
  useAdminDashboardRevision,
  useAdminWatchingPresence
} from "@/data/useAdminWatchingLive";
import {
  INITIAL_TOP_DANCE_COUNT,
  visibleTopDances
} from "@/data/adminDanceDisclosure";
import { Button } from "@/design/Button";
import { ChevronRightIcon, HeartIcon } from "@/design/icons";
import { Card, Chip, ContentSection, Screen } from "@/design/primitives";
import { Muted, Overline, Text } from "@/design/Text";
import { fonts, hoverRing, NARROW_BREAKPOINT, palette, radius, space } from "@/design/tokens";
import { UtilityChip } from "@/design/UtilityChip";
import { useAppRouter } from "@/lib/appRouter";
import { displayTitle } from "@/lib/format";

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function fmtLastActive(iso: string | null): string {
  if (!iso) return "Never";
  const ms = Math.max(0, Date.now() - new Date(iso).getTime());
  const minutesAgo = ms / 60_000;
  if (minutesAgo < 2) return "Just now";
  if (minutesAgo < 60) return `${Math.floor(minutesAgo)} min ago`;
  const hoursAgo = minutesAgo / 60;
  if (hoursAgo < 24) return `${Math.floor(hoursAgo)}h ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function billingChip(status: string): { tone: "accent" | "neutral" | "danger"; label: string } {
  if (status === "active") return { tone: "accent", label: "Renews" };
  if (status === "canceling") return { tone: "neutral", label: "Renewal off" };
  if (status === "past_due") return { tone: "danger", label: "Payment issue" };
  return { tone: "neutral", label: "No renewal" };
}

/**
 * `compare` is a plain reference value, never a percentage: before evening,
 * a partial day always loses to a finished one, so a delta would read as bad
 * news every morning and be ignored by noon.
 */
function StatCard({
  value,
  label,
  note,
  compare,
  compact
}: {
  value: string;
  label: string;
  note: string;
  compare?: string;
  compact: boolean;
}) {
  return (
    <Card style={[styles.statCard, compact && styles.statCardCompact]}>
      <Overline>{label}</Overline>
      <Text variant="display" style={styles.statValue}>{value}</Text>
      {/* Two tiles to a row on phones leaves no width for a single line. */}
      <Muted variant="small" numberOfLines={compact ? 2 : 1}>{note}</Muted>
      {compare ? (
        <Text variant="small" color={palette.faint} numberOfLines={compact ? 2 : 1}>
          {compare}
        </Text>
      ) : null}
    </Card>
  );
}

/** Lazy per-dance retention block; mounts (and fetches) only when expanded. */
function DanceRetention({
  dance,
  familyAccountId,
  compact
}: {
  dance: AdminDanceSummary;
  familyAccountId?: string;
  compact: boolean;
}) {
  const { data, loading, error } = useAdminDanceRetention(dance, familyAccountId);
  if (loading) {
    return (
      <View style={styles.retentionPending}>
        <Muted variant="small">Loading retention…</Muted>
      </View>
    );
  }
  if (error || !data) {
    return (
      <View style={styles.retentionPending}>
        <Muted variant="small">Retention couldn’t load. Try again in a moment.</Muted>
      </View>
    );
  }
  return (
    <>
      <RetentionCurve
        idleSummary={`${dance.plays} ${dance.plays === 1 ? "play" : "plays"} · ${
          fmtHours(dance.watchHours)
        } watched · Last watched ${fmtLastActive(dance.lastWatchedAt)}`}
        retention={data}
      />
      {!familyAccountId && data.families ? (
        <RetentionFamilyBreakdown
          compact={compact}
          durationSeconds={data.durationSeconds}
          families={data.families}
          storyboardVttUrl={data.storyboardVttUrl}
        />
      ) : null}
    </>
  );
}

function TopDances({
  dances,
  compact,
  familyAccountId
}: {
  dances: AdminDanceSummary[];
  compact: boolean;
  familyAccountId?: string;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const maxWatchHours = Math.max(0.01, ...dances.map((dance) => dance.watchHours));

  if (dances.length === 0) {
    return (
      <Card>
        <Muted>Watch data appears here after the first plays.</Muted>
      </Card>
    );
  }

  return (
    <Card style={styles.listCard}>
      {dances.map((dance, index) => {
        const danceKey = `${dance.recitalId}/${dance.performanceId}/${dance.danceId}`;
        const expanded = expandedId === danceKey;
        return (
          <View key={danceKey}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`${displayTitle(dance.title)} — ${dance.plays} plays. ${
                expanded ? "Hide" : "Show"
              } retention.`}
              onPress={() => setExpandedId(expanded ? null : danceKey)}
              style={({ hovered }) => [
                styles.danceRow,
                compact && styles.danceRowCompact,
                index > 0 && styles.rowDivider,
                hovered && styles.rowHovered
              ]}
            >
              <View style={[styles.rank, compact && styles.rankCompact]}>
                <Text variant="overline" color={palette.faint}>
                  {String(index + 1).padStart(2, "0")}
                </Text>
              </View>
              <View style={styles.danceMain}>
                <View style={styles.danceHeaderRow}>
                  <View style={styles.danceIdentity}>
                    <Text variant="danceTitle" numberOfLines={1}>
                      {displayTitle(dance.title)}
                    </Text>
                    <Muted variant="small" numberOfLines={1}>
                      {dance.performanceTitle}
                      {danceStyleLabel(dance)
                        ? ` · ${danceStyleLabel(dance)}`
                        : ""}
                    </Muted>
                  </View>
                  <View style={[styles.danceMetrics, compact && styles.danceMetricsCompact]}>
                    <Text variant="bodyStrong" color={palette.textSoft}>
                      {dance.plays}
                      <Muted variant="small"> plays</Muted>
                    </Text>
                    <Muted variant="small" numberOfLines={1}>
                      {fmtHours(dance.watchHours)} watched · Last watched{" "}
                      {fmtLastActive(dance.lastWatchedAt)}
                    </Muted>
                  </View>
                </View>
                <View style={styles.meterTrack}>
                  <View
                    style={[
                      styles.meterFill,
                      { width: `${(dance.watchHours / maxWatchHours) * 100}%` }
                    ]}
                  />
                </View>
              </View>
            </Pressable>
            {expanded ? (
              <View style={[styles.retentionWrap, compact && styles.retentionWrapCompact]}>
                <DanceRetention
                  dance={dance}
                  familyAccountId={familyAccountId}
                  compact={compact}
                />
              </View>
            ) : null}
          </View>
        );
      })}
    </Card>
  );
}

function MostWatched({
  dances,
  compact
}: {
  dances: AdminDanceSummary[];
  compact: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const canExpand = dances.length > INITIAL_TOP_DANCE_COUNT;
  const visibleDances = visibleTopDances(dances, showAll);
  const sectionLabel = canExpand
    ? showAll
      ? "All watched"
      : `${INITIAL_TOP_DANCE_COUNT} most watched`
    : "Most watched";

  return (
    <ContentSection
      header={
        <View style={styles.sectionHeader}>
          <Overline>{sectionLabel}</Overline>
          {canExpand ? (
            <UtilityChip
              label={showAll ? "Show top 15" : `Show all (${dances.length})`}
              onPress={() => setShowAll((current) => !current)}
            />
          ) : null}
        </View>
      }
    >
      <TopDances dances={visibleDances} compact={compact} />
    </ContentSection>
  );
}

function FamilyDances({
  familyAccountId,
  compact,
  refreshKey
}: {
  familyAccountId: string;
  compact: boolean;
  refreshKey: string;
}) {
  const { data, loading, error } = useAdminFamilyDances(familyAccountId, refreshKey);
  if (loading) {
    return <Muted variant="small">Loading watched dances…</Muted>;
  }
  if (error) {
    return <Muted variant="small">Watched dances couldn’t load. Try again in a moment.</Muted>;
  }
  if (!data || data.dances.length === 0) {
    return <Muted variant="small">No dance watches recorded yet.</Muted>;
  }
  return (
    <TopDances
      dances={data.dances}
      compact={compact}
      familyAccountId={familyAccountId}
    />
  );
}

function FamilyOverviewItem({
  label,
  plays,
  watchHours,
  index,
  compact
}: {
  label: string;
  plays: number;
  watchHours: number;
  index: number;
  compact: boolean;
}) {
  return (
    <View style={[
      styles.familyOverviewItem,
      !compact && styles.familyOverviewDivider,
      compact && styles.familyOverviewItemCompact,
      compact && index % 2 === 1 && styles.familyOverviewDivider,
      compact && index > 1 && styles.familyOverviewRowDivider
    ]}>
      <Overline>{label}</Overline>
      <Text variant="bodyStrong">{plays} {plays === 1 ? "play" : "plays"}</Text>
      <Muted variant="small">{fmtHours(watchHours)} watched</Muted>
    </View>
  );
}

function FamilyOverview({
  family,
  compact
}: {
  family: AdminDashboardData["families"][number];
  compact: boolean;
}) {
  const dancers = family.myDancers;
  const activity = family.activity;
  return (
    <View style={[styles.familyOverview, compact && styles.familyOverviewCompact]}>
      <View style={[
        styles.familyOverviewItem,
        compact && styles.familyOverviewItemCompact
      ]}>
        <View style={styles.familyMyDancersHeader}>
          <View style={styles.familyMyDancersTitle}>
            <HeartIcon size={14} color={palette.pink} />
            <Text variant="overline" color={palette.pink}>My dancers</Text>
          </View>
          <Chip tone="pink">{dancers.length}</Chip>
        </View>
        {dancers.length > 0 ? (
          <View style={styles.familyMyDancersList}>
            {dancers.map((dancer) => (
              <Chip
                key={`${dancer.recitalId}/${dancer.dancerId}`}
                tone="pink"
                icon={<HeartIcon size={11} color={palette.pink} />}
              >
                {dancer.name}
              </Chip>
            ))}
          </View>
        ) : (
          <Muted variant="small">None saved</Muted>
        )}
      </View>
      <FamilyOverviewItem
        label="7 days"
        plays={activity.plays7d}
        watchHours={activity.watchHours7d}
        index={1}
        compact={compact}
      />
      <FamilyOverviewItem
        label="30 days"
        plays={activity.plays30d}
        watchHours={activity.watchHours30d}
        index={2}
        compact={compact}
      />
      <FamilyOverviewItem
        label="1 year"
        plays={activity.plays365d}
        watchHours={activity.watchHours365d}
        index={3}
        compact={compact}
      />
    </View>
  );
}

function PlayTesters({
  playTesters,
  compact
}: {
  playTesters: AdminDashboardData["playTesters"];
  compact: boolean;
}) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const uniqueEmails = Array.from(
    new Set(playTesters.submissions.map((submission) => submission.googleEmail))
  );

  async function copyAll() {
    try {
      const clipboard = globalThis.navigator?.clipboard;
      if (!clipboard) throw new Error("Clipboard is unavailable.");
      await clipboard.writeText(uniqueEmails.join(", "));
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }

  return (
    <ContentSection
      header={
        <View style={styles.sectionHeader}>
          <View style={styles.playTesterTitle}>
            <Overline>Play testers</Overline>
            <Chip tone="accent">{playTesters.uniqueCount} unique</Chip>
          </View>
          <Button
            label={copyState === "copied" ? "Copied" : "Copy all"}
            variant="secondary"
            disabled={uniqueEmails.length === 0}
            onPress={() => void copyAll()}
          />
        </View>
      }
    >
      {playTesters.submissions.length === 0 ? (
        <Card>
          <Muted>Google account submissions appear here from the Android page.</Muted>
        </Card>
      ) : (
        <Card style={styles.listCard}>
          {playTesters.submissions.map((submission, index) => (
            <View
              key={submission.familyAccountId}
              style={[
                styles.playTesterRow,
                compact && styles.playTesterRowCompact,
                index > 0 && styles.rowDivider
              ]}
            >
              <View style={styles.playTesterIdentity}>
                <Text variant="bodyStrong" numberOfLines={1}>
                  {submission.purchaseEmail}
                </Text>
                <Muted variant="small" numberOfLines={1}>
                  {submission.familyAccountId}
                </Muted>
              </View>
              <View style={styles.playTesterGoogle}>
                {compact ? <Overline>Google account</Overline> : null}
                <Text variant="smallStrong" color={palette.textSoft} numberOfLines={1}>
                  {submission.googleEmail}
                </Text>
              </View>
              <View
                style={[
                  styles.playTesterTimestamp,
                  compact && styles.playTesterTimestampCompact
                ]}
              >
                {compact ? <Overline>Submitted</Overline> : null}
                <Muted variant="small">{fmtDateTime(submission.submittedAt)}</Muted>
              </View>
            </View>
          ))}
        </Card>
      )}
      {copyState === "failed" ? (
        <Muted variant="small">
          Clipboard access is unavailable here. Open the dashboard in a browser to copy the list.
        </Muted>
      ) : null}
    </ContentSection>
  );
}

function Families({
  families,
  compact,
  refreshKey
}: {
  families: AdminDashboardData["families"];
  compact: boolean;
  refreshKey: string;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  if (families.length === 0) {
    return (
      <Card>
        <Muted>Families appear here once accounts are claimed.</Muted>
      </Card>
    );
  }
  return (
    <Card style={styles.listCard}>
      {families.map((family, index) => {
        const billing = billingChip(family.billingStatus);
        const expanded = expandedId === family.familyAccountId;
        const accessSummary = `${family.accessUntil
          ? `Access until ${fmtDate(family.accessUntil)}`
          : "No access window"} · ${family.deviceCount} ${
          family.deviceCount === 1 ? "device" : "devices"
        } · ${family.swapsRemaining} ${
          family.swapsRemaining === 1 ? "swap" : "swaps"
        } left`;
        const recentActivity =
          `Last active ${fmtLastActive(family.lastActiveAt)} · ` +
          `Last watched ${fmtLastActive(family.lastWatchedAt)}`;
        return (
          <View key={family.familyAccountId}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`${family.email}. ${expanded ? "Hide" : "Show"} watched dances.`}
              accessibilityState={{ expanded }}
              onPress={() => setExpandedId(expanded ? null : family.familyAccountId)}
              style={({ hovered }) => [
                styles.familyRow,
                compact && styles.familyRowCompact,
                index > 0 && styles.rowDivider,
                hovered && styles.rowHovered
              ]}
            >
              {compact ? (
                <View style={styles.familyMain}>
                  <View style={styles.familyCompactHeader}>
                    <Text variant="bodyStrong" numberOfLines={1} style={styles.familyCompactEmail}>
                      {family.email}
                    </Text>
                    <View style={styles.familyCompactActions}>
                      <Chip tone={billing.tone === "neutral" ? undefined : billing.tone} dot>
                        {billing.label}
                      </Chip>
                      <View style={[styles.familyChevron, expanded && styles.familyChevronExpanded]}>
                        <ChevronRightIcon size={16} color={palette.muted} />
                      </View>
                    </View>
                  </View>
                  <Muted variant="small" numberOfLines={1}>{accessSummary}</Muted>
                  <View style={styles.familyCompactContext}>
                    <Text variant="smallStrong" color={palette.textSoft}>
                      {fmtHours(family.watchHours)} watched
                    </Text>
                    <Muted variant="small">
                      {recentActivity}
                    </Muted>
                  </View>
                </View>
              ) : (
                <>
                  <View style={styles.familyMain}>
                    <Text variant="bodyStrong" numberOfLines={1}>{family.email}</Text>
                    <Muted variant="small" numberOfLines={1}>{accessSummary}</Muted>
                  </View>
                  <View style={styles.familyMetrics}>
                    <Chip tone={billing.tone === "neutral" ? undefined : billing.tone} dot>
                      {billing.label}
                    </Chip>
                    <View style={styles.familyNumbers}>
                      <Text variant="smallStrong" color={palette.textSoft}>
                        {fmtHours(family.watchHours)} watched
                      </Text>
                      <Muted variant="small">
                        {recentActivity}
                      </Muted>
                    </View>
                    <View style={[styles.familyChevron, expanded && styles.familyChevronExpanded]}>
                      <ChevronRightIcon size={16} color={palette.muted} />
                    </View>
                  </View>
                </>
              )}
            </Pressable>
            {expanded ? (
              <View style={[styles.familyDancesWrap, compact && styles.familyDancesWrapCompact]}>
                <FamilyOverview family={family} compact={compact} />
                <View style={styles.familyDancesSection}>
                  <View style={styles.familyDancesHeading}>
                    <Overline>Watched dances</Overline>
                  </View>
                  <FamilyDances
                    familyAccountId={family.familyAccountId}
                    compact={compact}
                    refreshKey={refreshKey}
                  />
                </View>
              </View>
            ) : null}
          </View>
        );
      })}
    </Card>
  );
}

type AdminTab = "watching" | "email";
type EmailFilter = "all" | "opened" | "signed-in" | "opened-no-sign-in" | "failed";
type EmailScope = {
  id: string;
  kind: "campaign" | "operational";
  label: string;
  detail: string;
  messages: AdminEmailMessage[];
};

const OPERATIONAL_EMAIL_SCOPE = "operational";

function humanEmailEngagement(message: AdminEmailMessage): boolean {
  return message.humanOpenCount > 0 || message.humanClickCount > 0;
}

function distinctEmailRecipients(
  messages: AdminEmailMessage[],
  predicate: (message: AdminEmailMessage) => boolean
): number {
  return new Set(
    messages.filter(predicate).map((message) => message.recipient)
  ).size;
}

function emailScopes(messages: AdminEmailMessage[]): EmailScope[] {
  const campaigns = new Map<string, AdminEmailMessage[]>();
  for (const message of messages) {
    if (message.template !== "campaign" || !message.campaign) continue;
    const grouped = campaigns.get(message.campaign) ?? [];
    grouped.push(message);
    campaigns.set(message.campaign, grouped);
  }
  const campaignScopes = [...campaigns.entries()]
    .map(([campaign, campaignMessages]) => {
      const newest = campaignMessages.reduce((current, message) =>
        message.createdAt > current.createdAt ? message : current
      );
      const sent = distinctEmailRecipients(
        campaignMessages,
        (message) => message.acceptedAt != null
      );
      return {
        id: `campaign:${campaign}`,
        kind: "campaign" as const,
        label: newest.subject,
        detail: `${fmtDate(newest.createdAt)} · ${sent} sent`,
        messages: campaignMessages,
        newestAt: newest.createdAt
      };
    })
    .sort((left, right) => right.newestAt.localeCompare(left.newestAt))
    .map(({newestAt: _newestAt, ...scope}) => scope);
  const operationalMessages = messages.filter((message) =>
    message.template !== "campaign" || !message.campaign
  );
  if (operationalMessages.length === 0) return campaignScopes;
  const sent = distinctEmailRecipients(
    operationalMessages,
    (message) => message.acceptedAt != null
  );
  return [
    ...campaignScopes,
    {
      id: OPERATIONAL_EMAIL_SCOPE,
      kind: "operational",
      label: "Operational emails",
      detail: `Sign-in and account notices · ${sent} sent`,
      messages: operationalMessages
    }
  ];
}

function DashboardTabs({selected, onSelect}: {
  selected: AdminTab;
  onSelect: (tab: AdminTab) => void;
}) {
  return (
    <View accessibilityRole="tablist" style={styles.tabRail}>
      {([
        ["watching", "Watching"],
        ["email", "Email"]
      ] as const).map(([value, label]) => (
        <Pressable
          key={value}
          accessibilityRole="tab"
          accessibilityState={{selected: selected === value}}
          onPress={() => onSelect(value)}
          style={({hovered}) => [
            styles.tab,
            selected === value && styles.tabSelected,
            hovered && selected !== value && styles.tabHovered
          ]}
        >
          <Text
            variant="smallStrong"
            color={selected === value ? palette.text : palette.muted}
          >
            {label}
          </Text>
        </Pressable>
      ))}
    </View>
  );
}

function emailStatus(message: AdminEmailMessage): {
  label: string;
  tone: "neutral" | "success" | "warning" | "danger";
} {
  if (message.complainedAt) return {label: "Complaint", tone: "danger"};
  if (message.unsubscribedAt) return {label: "Unsubscribed", tone: "warning"};
  if (message.permanentlyFailedAt) return {label: "Failed", tone: "danger"};
  if (message.humanClickCount > 0) return {label: "Clicked", tone: "success"};
  if (message.humanOpenCount > 0) return {label: "Opened", tone: "success"};
  if (message.automatedOpenCount > 0 || message.automatedClickCount > 0) {
    return {label: "Automated open", tone: "warning"};
  }
  if (message.deliveredAt) return {label: "Delivered", tone: "neutral"};
  if (message.temporaryFailedAt) return {label: "Delayed", tone: "warning"};
  return {label: message.acceptedAt ? "Sent" : "Queued", tone: "neutral"};
}

function EmailJourney({message}: {message: AdminEmailMessage}) {
  const engaged = message.humanOpenCount > 0 || message.humanClickCount > 0;
  const stages = [
    ["Sent", message.acceptedAt != null],
    ["Delivered", message.deliveredAt != null],
    ["Opened", engaged],
    ["Signed in", message.signedInAt != null]
  ] as const;
  return (
    <View style={styles.emailJourney}>
      {stages.map(([label, complete], index) => (
        <View key={label} style={styles.emailJourneyStage}>
          {index > 0 ? (
            <View style={[
              styles.emailJourneyLine,
              complete && styles.emailJourneyLineComplete
            ]} />
          ) : null}
          <View style={[
            styles.emailJourneyDot,
            complete && styles.emailJourneyDotComplete
          ]} />
          <Text
            variant="small"
            color={complete ? palette.textSoft : palette.faint}
            numberOfLines={1}
          >
            {label}
          </Text>
        </View>
      ))}
    </View>
  );
}

function EmailActivity({
  activity,
  compact
}: {
  activity: NonNullable<AdminDashboardData["emailActivity"]>;
  compact: boolean;
}) {
  const [filter, setFilter] = useState<EmailFilter>("all");
  const scopes = emailScopes(activity.messages);
  const [selectedScopeId, setSelectedScopeId] = useState<string | null>(null);
  const [scopeMenuOpen, setScopeMenuOpen] = useState(false);
  const selectedScope = scopes.find((scope) => scope.id === selectedScopeId) ?? scopes[0];
  const scopeMessages = selectedScope?.messages ?? [];
  const sent = distinctEmailRecipients(scopeMessages, (message) => message.acceptedAt != null);
  const delivered = distinctEmailRecipients(scopeMessages, (message) => message.deliveredAt != null);
  const opened = distinctEmailRecipients(scopeMessages, humanEmailEngagement);
  const clicked = distinctEmailRecipients(
    scopeMessages,
    (message) => message.humanClickCount > 0
  );
  const failed = distinctEmailRecipients(
    scopeMessages,
    (message) => message.permanentlyFailedAt != null
  );
  const delayed = distinctEmailRecipients(
    scopeMessages,
    (message) => message.temporaryFailedAt != null && message.permanentlyFailedAt == null
  );
  const automatedOnly = distinctEmailRecipients(
    scopeMessages,
    (message) =>
      !humanEmailEngagement(message) &&
      (message.automatedOpenCount > 0 || message.automatedClickCount > 0)
  );
  const signedIn = distinctEmailRecipients(
    scopeMessages,
    (message) => message.acceptedAt != null && message.signedInAt != null
  );
  const openedNotSignedIn = distinctEmailRecipients(
    scopeMessages,
    (message) => humanEmailEngagement(message) && message.signedInAt == null
  );
  const messages = scopeMessages.filter((message) => {
    if (filter === "opened") return humanEmailEngagement(message);
    if (filter === "signed-in") return message.signedInAt != null;
    if (filter === "opened-no-sign-in") {
      return humanEmailEngagement(message) && message.signedInAt == null;
    }
    if (filter === "failed") return message.permanentlyFailedAt != null;
    return true;
  });
  const filters: Array<[EmailFilter, string]> = selectedScope?.kind === "operational"
    ? [
        ["all", `All ${scopeMessages.length}`],
        ["failed", `Failed ${failed}`]
      ]
    : [
        ["all", `All ${scopeMessages.length}`],
        ["opened", `Opened ${opened}`],
        ["signed-in", `Signed in ${signedIn}`],
        ["opened-no-sign-in", `Opened, no sign-in ${openedNotSignedIn}`]
      ];
  const selectScope = (scopeId: string) => {
    setSelectedScopeId(scopeId);
    setScopeMenuOpen(false);
    setFilter("all");
  };

  return (
    <>
      {selectedScope ? (
        <ContentSection
          style={[styles.emailSection, styles.emailCampaignSection]}
          header={
            <View style={[
              styles.emailScopeHeader,
              compact && styles.emailScopeHeaderCompact,
              scopeMenuOpen && styles.emailScopeHeaderOpen
            ]}>
              <View style={styles.emailScopeIntro}>
                <Overline>
                  {selectedScope.kind === "campaign" ? "Campaign performance" : "Operational delivery"}
                </Overline>
                <Muted variant="small">
                  {selectedScope.kind === "campaign"
                    ? "The latest campaign is selected automatically."
                    : "Sign-in links and account notices stay separate from campaign results."}
                </Muted>
              </View>
              <View style={[
                styles.emailScopePicker,
                compact && styles.emailScopePickerCompact,
                scopeMenuOpen && styles.emailScopePickerOpen
              ]}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityState={{expanded: scopeMenuOpen}}
                  onPress={() => setScopeMenuOpen((open) => !open)}
                  style={({hovered}) => [
                    styles.emailScopeButton,
                    hovered && styles.emailScopeButtonHovered
                  ]}
                >
                  <View style={styles.emailScopeButtonText}>
                    <Text variant="smallStrong" numberOfLines={1}>{selectedScope.label}</Text>
                    <Text
                      variant="small"
                      color={palette.muted}
                      numberOfLines={1}
                      style={styles.emailScopeDetail}
                    >
                      {selectedScope.detail}
                    </Text>
                  </View>
                  <View style={[
                    styles.emailScopeChevron,
                    scopeMenuOpen && styles.emailScopeChevronOpen
                  ]}>
                    <ChevronRightIcon size={16} color={palette.muted} />
                  </View>
                </Pressable>
                {scopeMenuOpen ? (
                  <View accessibilityRole="menu" style={styles.emailScopeMenu}>
                    {scopes.map((scope) => {
                      const selected = scope.id === selectedScope.id;
                      return (
                        <Pressable
                          key={scope.id}
                          accessibilityRole="menuitem"
                          accessibilityState={{selected}}
                          onPress={() => selectScope(scope.id)}
                          style={({hovered}) => [
                            styles.emailScopeOption,
                            selected && styles.emailScopeOptionSelected,
                            hovered && !selected && styles.tabHovered
                          ]}
                        >
                          <Text
                            variant="smallStrong"
                            color={selected ? palette.text : palette.textSoft}
                            numberOfLines={1}
                          >
                            {scope.label}
                          </Text>
                          <Text
                            variant="small"
                            color={palette.muted}
                            numberOfLines={1}
                            style={styles.emailScopeDetail}
                          >
                            {scope.detail}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                ) : null}
              </View>
            </View>
          }
        >
      <View style={styles.statsGrid}>
        {selectedScope?.kind === "operational" ? (
          <>
            <StatCard
              value={String(sent)}
              label="Sent"
              note={`${delivered} delivered`}
              compare={delayed > 0 ? `${delayed} delayed` : "No delays"}
              compact={compact}
            />
            <StatCard
              value={String(delivered)}
              label="Delivered"
              note="Accepted by recipient server"
              compare={`${scopeMessages.length} messages recorded`}
              compact={compact}
            />
            <StatCard
              value={String(failed)}
              label="Failed"
              note="Permanent delivery failures"
              compare={failed > 0 ? "Review the addresses below" : "No failures"}
              compact={compact}
            />
            <StatCard
              value={sent > 0 ? `${Math.round(delivered / sent * 100)}%` : "—"}
              label="Delivery rate"
              note="Operational email health"
              compare="Campaigns excluded"
              compact={compact}
            />
          </>
        ) : (
          <>
            <StatCard
              value={String(sent)}
              label="Sent"
              note={`${delivered} delivered`}
              compare={failed > 0 ? `${failed} failed` : "No failures"}
              compact={compact}
            />
            <StatCard
              value={String(opened)}
              label="Opened"
              note={`${clicked} clicked through`}
              compare={
                automatedOnly > 0
                  ? `${automatedOnly} automated opens excluded`
                  : "Automated opens excluded"
              }
              compact={compact}
            />
            <StatCard
              value={String(signedIn)}
              label="Signed in"
              note={`of ${sent} campaign recipients`}
              compare="Matched by recipient email"
              compact={compact}
            />
            <StatCard
              value={String(openedNotSignedIn)}
              label="Opened · no sign-in"
              note="Best follow-up list"
              compare="Ready for follow-up"
              compact={compact}
            />
          </>
        )}
      </View>
        </ContentSection>
      ) : null}

      <ContentSection
        style={styles.emailSection}
        header={
          <View style={[styles.emailListHeader, compact && styles.emailListHeaderCompact]}>
            <View style={styles.emailSectionTitle}>
              <Overline>Email activity</Overline>
              <Muted variant="small">
                {selectedScope?.kind === "operational"
                  ? "Delivery records for sign-in links and account notices."
                  : "Delivery, opens, clicks, and sign-ins for this campaign."}
              </Muted>
            </View>
            <View style={styles.emailFilters}>
              {filters.map(([value, label]) => (
                <Pressable
                  key={value}
                  accessibilityRole="button"
                  accessibilityState={{selected: filter === value}}
                  onPress={() => setFilter(value)}
                  style={({hovered}) => [
                    styles.emailFilter,
                    filter === value && styles.emailFilterSelected,
                    hovered && filter !== value && styles.tabHovered
                  ]}
                >
                  <Text
                    variant="smallStrong"
                    color={filter === value ? palette.text : palette.muted}
                  >
                    {label}
                  </Text>
                </Pressable>
              ))}
            </View>
          </View>
        }
      >
        {messages.length === 0 ? (
          <Card>
            <Muted>No emails match this view yet.</Muted>
          </Card>
        ) : (
          <Card style={styles.listCard}>
            {messages.map((message, index) => {
              const status = emailStatus(message);
              const needsSignIn =
                selectedScope?.kind === "campaign" &&
                (message.humanOpenCount > 0 || message.humanClickCount > 0) &&
                message.signedInAt == null;
              return (
                <View
                  key={message.id}
                  style={[
                    styles.emailRow,
                    compact && styles.emailRowCompact,
                    index > 0 && styles.rowDivider
                  ]}
                >
                  <View style={styles.emailIdentity}>
                    <View style={styles.emailRecipientRow}>
                      <Text variant="bodyStrong" numberOfLines={1} style={styles.emailRecipient}>
                        {message.recipient}
                      </Text>
                      <Chip tone={status.tone} dot>{status.label}</Chip>
                      {needsSignIn ? <Chip tone="warning">No sign-in</Chip> : null}
                    </View>
                    <Muted variant="small" numberOfLines={1}>{message.subject}</Muted>
                    <Text variant="small" color={palette.faint} numberOfLines={1}>
                      {message.campaign ?? message.template}
                    </Text>
                    {compact && selectedScope?.kind === "campaign" ? (
                      <EmailJourney message={message} />
                    ) : null}
                  </View>
                  {!compact && selectedScope?.kind === "campaign" ? (
                    <EmailJourney message={message} />
                  ) : null}
                  <View style={[styles.emailTime, compact && styles.emailTimeCompact]}>
                    <Muted variant="small">{fmtDateTime(message.createdAt)}</Muted>
                    {message.failureReason ? (
                      <Text variant="small" color={palette.danger} numberOfLines={1}>
                        {message.failureReason}
                      </Text>
                    ) : message.lastOpenedAt ? (
                      <Text variant="small" color={palette.textSoft}>
                        Opened {fmtLastActive(message.lastOpenedAt)}
                      </Text>
                    ) : null}
                  </View>
                </View>
              );
            })}
          </Card>
        )}
      </ContentSection>
    </>
  );
}

export default function AdminScreen() {
  const router = useAppRouter();
  const { view } = useLocalSearchParams<{view?: string | string[]}>();
  const { width } = useWindowDimensions();
  const compact = width < NARROW_BREAKPOINT;
  const { isAdmin, devMode, demoMode, loading: authLoading } = useAuthState();
  const selectedView = Array.isArray(view) ? view[0] : view;
  const tab: AdminTab = selectedView === "email" ? "email" : "watching";
  const { data: snapshotData, loading, error, refresh } = useAdminDashboard();
  const watchingPresence = useAdminWatchingPresence(tab === "watching");
  useAdminDashboardRevision({
    enabled: tab === "watching",
    onRevision: () => void refresh()
  });
  const data = useMemo<AdminDashboardData | null>(() => {
    if (!snapshotData || !watchingPresence) return snapshotData;
    return {
      ...snapshotData,
      totals: {
        ...snapshotData.totals,
        watchingNow: watchingPresence.watchingNow
      },
      families: snapshotData.families.map((family) => {
        const sessionActivity = watchingPresence.lastSessionActivityByFamily.get(
          family.familyAccountId
        );
        if (!sessionActivity || (family.lastActiveAt && family.lastActiveAt >= sessionActivity)) {
          return family;
        }
        return {...family, lastActiveAt: sessionActivity};
      })
    };
  }, [snapshotData, watchingPresence]);
  const emailActivity = useAdminEmailActivity({
    enabled: tab === "email",
    fallback: data?.emailActivity ?? null
  });
  const totals = data?.totals;
  const today = totals?.today;
  const yesterday = totals?.yesterday;
  const todayPlayCount = data?.dailyWatch?.[data.dailyWatch.length - 1]?.playCount;
  const yesterdayPlayCount = data?.dailyWatch?.[data.dailyWatch.length - 2]?.playCount;
  const everWatched = totals?.familiesEverWatched;
  const unclaimedInvites = totals?.unclaimedInvites ?? 0;
  // The dance behind the most recent play: what the room is on when someone is
  // watching, where the night ended when nobody is. Said nowhere else up here.
  const newestPlay = data?.topDances.reduce<AdminDanceSummary | null>(
    (newest, dance) =>
      dance.lastWatchedAt != null &&
      (newest?.lastWatchedAt == null || dance.lastWatchedAt > newest.lastWatchedAt)
        ? dance
        : newest,
    null
  ) ?? null;
  const neverWatched =
    everWatched == null || totals == null ? null : totals.families - everWatched;
  // Local design preview: no-config dev, or dev-build demo browsing. A store
  // build's demo mode never passes this gate — __DEV__ is false there.
  const allowed = isAdmin || devMode || (__DEV__ && demoMode);

  useEffect(() => {
    if (shouldRedirectFromAdmin(authLoading, allowed)) {
      router.replace("/account" as never);
    }
  }, [allowed, authLoading, router]);

  if (authLoading || !allowed) return null;

  return (
    <Screen wide enter="rise" header={<AppHeader back />}>
      <View style={styles.header}>
        <View style={styles.headerTitle}>
          <Overline>Studio admin</Overline>
          <Text variant="display">Dashboard</Text>
        </View>
        <DashboardTabs
          selected={tab}
          onSelect={(nextTab) => router.setParams({
            view: nextTab === "email" ? "email" : undefined
          })}
        />
      </View>

      {loading && !data ? (
        <Card>
          <Muted>Gathering the numbers…</Muted>
        </Card>
      ) : error && !data ? (
        <Card>
          <Text variant="body" color={palette.danger} style={styles.errorText}>
            The dashboard couldn’t load.
          </Text>
          <Button label="Try again" variant="secondary" onPress={refresh} />
        </Card>
      ) : data && tab === "email" ? (
        emailActivity ? (
          <EmailActivity activity={emailActivity} compact={compact} />
        ) : (
          <Card style={styles.tabEmptyCard}>
            <Text variant="bodyStrong">Email tracking is coming online.</Text>
            <Muted>Refresh after the updated backend is deployed.</Muted>
          </Card>
        )
      ) : data ? (
        <>
          <View style={styles.statsGrid}>
            <StatCard
              value={String(data.totals.watchingNow)}
              label="Watching now"
              note={data.totals.lastWatchedAt
                ? `Last watch ${fmtWatchClock(data.totals.lastWatchedAt)}`
                : "No watching yet"}
              compare={newestPlay ? `on ${displayTitle(newestPlay.title)}` : undefined}
              compact={compact}
            />
            <StatCard
              value={fmtHours(today?.watchHours ?? 0)}
              label="Watched · today"
              note={today
                ? todayPlayCount == null
                  ? fmtFamilies(today.familyCount)
                  : `${fmtPlays(todayPlayCount)} · ${fmtFamilies(today.familyCount)}`
                : "Updating"}
              compare={yesterday
                ? yesterdayPlayCount == null
                  ? `${fmtFamilies(yesterday.familyCount)} yesterday`
                  : `${fmtPlays(yesterdayPlayCount)} yesterday (${fmtFamilies(yesterday.familyCount)})`
                : undefined}
              compact={compact}
            />
            {/*
              Everyone handed access, counted once: watched + signed in but
              never watched + never signed in equals the denominator. A
              fraction over signed-in families alone would exclude the very
              group named on the line beneath it.
            */}
            <StatCard
              value={everWatched == null
                ? String(data.totals.families)
                : `${everWatched}/${data.totals.families + unclaimedInvites}`}
              label={everWatched == null ? "Families" : "Reach"}
              note={neverWatched == null
                ? `${data.totals.activeAccess} active access`
                : neverWatched > 0
                  ? `${neverWatched} signed in, but never watched`
                  : "Every signed-in family has watched"}
              compare={unclaimedInvites
                ? `${unclaimedInvites} invited, but never signed in`
                : neverWatched == null
                  ? undefined
                  : `${data.totals.activeAccess} with active access`}
              compact={compact}
            />
            <StatCard
              value={fmtHours(data.totals.watchHours7d)}
              label="Watched · 7 days"
              note={`${data.totals.plays7d} ${data.totals.plays7d === 1 ? "play" : "plays"}`}
              compare={data.totals.watchHoursPrev7d == null
                ? undefined
                : `Week before ${fmtHours(data.totals.watchHoursPrev7d)}`}
              compact={compact}
            />
          </View>

          {data.dailyWatch && data.dailyWatch.length > 0 ? (
            <ContentSection label="Daily watching">
              <DailyWatchTrend compact={compact} days={data.dailyWatch} />
            </ContentSection>
          ) : null}

          <MostWatched dances={data.topDances} compact={compact} />

          <ContentSection label="Families">
            <Families
              families={data.families}
              compact={compact}
              refreshKey={data.generatedAt}
            />
          </ContentSection>

          <PlayTesters playTesters={data.playTesters} compact={compact} />
        </>
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  header: {
    alignItems: "flex-end",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: space.lg,
    justifyContent: "space-between",
    marginBottom: space.xl,
    marginTop: 0
  },
  headerTitle: {
    gap: space.sm
  },
  tabRail: {
    backgroundColor: palette.coolWashFaint,
    borderColor: palette.hairline,
    borderRadius: radius.pill,
    borderWidth: 1,
    flexDirection: "row",
    gap: 3,
    padding: 3
  },
  tab: {
    borderRadius: radius.pill,
    paddingHorizontal: space.lg,
    paddingVertical: space.sm
  },
  tabSelected: {
    backgroundColor: palette.surfaceRaised
  },
  tabHovered: {
    backgroundColor: palette.coolWashHover
  },
  tabEmptyCard: {
    gap: space.xs
  },
  emailScopeHeader: {
    alignItems: "flex-end",
    flexDirection: "row",
    gap: space.xl,
    justifyContent: "space-between",
    position: "relative",
    zIndex: 1
  },
  emailScopeHeaderCompact: {
    alignItems: "stretch",
    flexDirection: "column",
    gap: space.md
  },
  emailScopeHeaderOpen: {
    zIndex: 20
  },
  emailScopeIntro: {
    flex: 1,
    gap: space.xs,
    minWidth: 0
  },
  emailScopePicker: {
    gap: space.xs,
    minWidth: 340,
    position: "relative"
  },
  emailScopePickerCompact: {
    alignSelf: "stretch",
    minWidth: 0
  },
  emailScopePickerOpen: {
    zIndex: 10
  },
  emailScopeButton: {
    alignItems: "center",
    backgroundColor: palette.coolWashFaint,
    borderColor: palette.hairline,
    borderRadius: radius.md,
    borderWidth: 1,
    flexDirection: "row",
    gap: space.md,
    justifyContent: "space-between",
    minHeight: 52,
    paddingHorizontal: space.md,
    paddingVertical: space.sm
  },
  emailScopeButtonHovered: {
    backgroundColor: palette.coolWashHover,
    ...hoverRing
  },
  emailScopeButtonText: {
    flex: 1,
    gap: 1,
    minWidth: 0
  },
  emailScopeDetail: {
    fontFamily: fonts.bodyMedium,
    fontSize: 11,
    letterSpacing: 0.35,
    lineHeight: 15,
    textTransform: "uppercase"
  },
  emailScopeChevron: {
    transform: [{rotate: "90deg"}]
  },
  emailScopeChevronOpen: {
    transform: [{rotate: "-90deg"}]
  },
  emailScopeMenu: {
    backgroundColor: palette.surfaceOverlay,
    borderColor: palette.hairlineStrong,
    borderRadius: radius.md,
    borderWidth: 1,
    boxShadow: "0 10px 28px rgba(0, 0, 0, 0.34)",
    left: 0,
    overflow: "hidden",
    position: "absolute",
    right: 0,
    top: "100%",
    transform: [{translateY: space.xs}],
    zIndex: 20
  },
  emailScopeOption: {
    gap: 1,
    paddingHorizontal: space.md,
    paddingVertical: space.md
  },
  emailScopeOptionSelected: {
    backgroundColor: palette.surfaceRaised
  },
  statsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: space.md,
    position: "relative",
    zIndex: 0
  },
  emailSection: {
    gap: space.xl
  },
  emailCampaignSection: {
    marginTop: 0
  },
  statCard: {
    flexBasis: 220,
    flexGrow: 1,
    gap: space.xs,
    minWidth: 0,
    padding: space.lg
  },
  statCardCompact: {
    flexBasis: "46%",
    padding: space.md
  },
  /** The eyebrow frames the number; the number owns the space under it. */
  statValue: {
    marginBottom: 2
  },
  sectionHeader: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.md,
    justifyContent: "space-between"
  },
  playTesterTitle: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: space.sm
  },
  playTesterRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.lg,
    paddingHorizontal: space.lg,
    paddingVertical: space.lg
  },
  playTesterRowCompact: {
    alignItems: "stretch",
    flexDirection: "column",
    gap: space.md,
    paddingHorizontal: space.md,
    paddingVertical: space.md
  },
  playTesterIdentity: {
    flex: 1.2,
    gap: 2,
    minWidth: 0
  },
  playTesterGoogle: {
    flex: 1,
    gap: space.xs,
    minWidth: 0
  },
  playTesterTimestamp: {
    alignItems: "flex-end",
    flexBasis: 190,
    flexShrink: 0,
    gap: space.xs
  },
  playTesterTimestampCompact: {
    alignItems: "flex-start",
    flexBasis: "auto"
  },
  listCard: {
    overflow: "hidden",
    padding: 0
  },
  emailListHeader: {
    alignItems: "flex-end",
    flexDirection: "row",
    gap: space.lg,
    justifyContent: "space-between"
  },
  emailListHeaderCompact: {
    alignItems: "stretch",
    flexDirection: "column"
  },
  emailSectionTitle: {
    flex: 1,
    gap: space.xs
  },
  emailFilters: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: space.sm
  },
  emailFilter: {
    borderColor: palette.hairline,
    borderRadius: radius.pill,
    borderWidth: 1,
    paddingHorizontal: space.md,
    paddingVertical: 6
  },
  emailFilterSelected: {
    backgroundColor: palette.surfaceRaised,
    borderColor: palette.hairlineStrong
  },
  emailRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.xl,
    paddingHorizontal: space.lg,
    paddingVertical: space.lg
  },
  emailRowCompact: {
    alignItems: "stretch",
    flexDirection: "column",
    gap: space.md,
    paddingHorizontal: space.md,
    paddingVertical: space.md
  },
  emailIdentity: {
    flex: 1.25,
    gap: 2,
    minWidth: 0
  },
  emailRecipientRow: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: space.sm
  },
  emailRecipient: {
    flexShrink: 1,
    minWidth: 120
  },
  emailJourney: {
    flex: 1,
    flexDirection: "row",
    maxWidth: 520,
    minWidth: 280,
    paddingTop: space.xs
  },
  emailJourneyStage: {
    alignItems: "center",
    flex: 1,
    gap: 5,
    minWidth: 0,
    position: "relative"
  },
  emailJourneyLine: {
    backgroundColor: palette.hairlineStrong,
    height: 2,
    position: "absolute",
    right: "50%",
    top: 4,
    width: "100%"
  },
  emailJourneyLineComplete: {
    backgroundColor: palette.accent
  },
  emailJourneyDot: {
    backgroundColor: palette.surfaceRaised,
    borderColor: palette.hairlineStrong,
    borderRadius: 5,
    borderWidth: 1,
    height: 10,
    width: 10
  },
  emailJourneyDotComplete: {
    backgroundColor: palette.accent,
    borderColor: palette.accent
  },
  emailTime: {
    alignItems: "flex-end",
    flexBasis: 190,
    flexShrink: 0,
    gap: 2
  },
  emailTimeCompact: {
    alignItems: "flex-start",
    flexBasis: "auto"
  },
  danceRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.lg,
    paddingHorizontal: space.lg,
    paddingVertical: space.lg
  },
  danceRowCompact: {
    alignItems: "flex-start",
    gap: space.sm,
    paddingHorizontal: space.md,
    paddingVertical: space.md
  },
  rowHovered: {
    backgroundColor: palette.coolWashSubtle
  },
  rowDivider: {
    borderTopColor: palette.hairline,
    borderTopWidth: 1
  },
  danceMain: {
    flex: 1,
    gap: space.sm,
    minWidth: 0
  },
  rank: {
    alignItems: "center",
    justifyContent: "center",
    width: 28
  },
  rankCompact: {
    paddingTop: 2,
    width: 24
  },
  danceHeaderRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.lg,
    justifyContent: "space-between"
  },
  danceIdentity: {
    flex: 1,
    gap: 2,
    minWidth: 0
  },
  danceMetrics: {
    alignItems: "flex-end",
    flexShrink: 0,
    gap: 2
  },
  danceMetricsCompact: {
    maxWidth: "48%"
  },
  meterTrack: {
    backgroundColor: palette.coolWashFaint,
    borderRadius: radius.pill,
    height: 4,
    overflow: "hidden"
  },
  meterFill: {
    backgroundColor: palette.accent,
    borderRadius: radius.pill,
    height: 4
  },
  retentionWrap: {
    paddingBottom: space.lg,
    paddingHorizontal: space.lg,
    paddingLeft: 28 + space.lg * 2,
    paddingTop: space.md
  },
  retentionWrapCompact: {
    paddingHorizontal: space.md,
    paddingLeft: 24 + space.md * 2
  },
  retentionPending: {
    alignItems: "center",
    paddingVertical: space.lg
  },
  familyRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.lg,
    justifyContent: "space-between",
    paddingHorizontal: space.lg,
    paddingVertical: space.lg
  },
  familyRowCompact: {
    alignItems: "stretch",
    flexDirection: "column",
    gap: 0
  },
  familyMain: {
    flex: 1,
    gap: 2
  },
  familyCompactHeader: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.md,
    justifyContent: "space-between"
  },
  familyCompactEmail: {
    flex: 1,
    minWidth: 0
  },
  familyCompactActions: {
    alignItems: "center",
    flexDirection: "row",
    flexShrink: 0,
    gap: space.sm
  },
  familyCompactContext: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.md,
    justifyContent: "space-between",
    marginTop: space.sm
  },
  familyMetrics: {
    alignItems: "flex-end",
    flexDirection: "row",
    gap: space.lg
  },
  familyNumbers: {
    alignItems: "flex-end",
    gap: 2,
    minWidth: 64
  },
  familyChevron: {
    alignItems: "center",
    alignSelf: "center",
    justifyContent: "center",
    width: 18,
    transform: [{ rotate: "0deg" }]
  },
  familyChevronExpanded: {
    transform: [{ rotate: "90deg" }]
  },
  familyDancesWrap: {
    backgroundColor: palette.coolWashFaint,
    borderTopColor: palette.hairline,
    borderTopWidth: 1,
    gap: space.lg,
    padding: space.lg
  },
  familyDancesWrapCompact: {
    padding: space.md
  },
  familyOverview: {
    backgroundColor: palette.surface,
    borderColor: palette.hairline,
    borderRadius: radius.md,
    borderWidth: 1,
    flexDirection: "row",
    overflow: "hidden"
  },
  familyOverviewCompact: {
    flexWrap: "wrap"
  },
  familyOverviewItem: {
    flex: 1,
    gap: space.xs,
    minWidth: 0,
    padding: space.lg
  },
  familyOverviewItemCompact: {
    flexBasis: "50%",
    flexGrow: 0,
    padding: space.md
  },
  familyOverviewDivider: {
    borderLeftColor: palette.hairline,
    borderLeftWidth: 1
  },
  familyOverviewRowDivider: {
    borderTopColor: palette.hairline,
    borderTopWidth: 1
  },
  familyMyDancersHeader: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between"
  },
  familyMyDancersTitle: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.sm
  },
  familyMyDancersList: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: space.sm
  },
  familyDancesHeading: {
    borderTopColor: palette.hairline,
    borderTopWidth: 1,
    paddingTop: space.lg
  },
  familyDancesSection: {
    gap: space.md
  },
  errorText: {
    marginBottom: space.md
  }
});
